// Standalone audience wizard backed by the same canonical resolver and live
// estimate endpoint used by the campaign audience builder.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Icons } from '@/components/primitives';
import { SearchableDropdown } from '@/components/dropdowns';
import { MutationErrorLine, QueryErrorNote } from '@/components/error-panel';
import {
  RequiredField,
  useRequiredFeedback,
  type RequiredIssue,
} from '@/components/required-field';
import { Section } from '@/components/wizard-section';
import type { Audience } from '@/api/types';
import {
  useAudiences,
  useCreateAudience,
  useProductions,
} from '@/api/hooks';
import {
  audienceIdForCreate,
  audienceTypeFromVisualType,
  visualGroupsFromCanonical,
  visualTypeFromAudienceType,
  type VisualAudienceGroup,
  type VisualAudienceType,
} from '@/lib/audience-visual-rules';
import { lookalikeFitPending } from '@/lib/lookalike-model';
import {
  AudienceBuilder,
  AudienceSizeGauge,
  defaultGroupsForType,
  TYPE_BLURB,
  type AudiencePreviewState,
} from './audience';
import { PageActions } from '@/components/page-actions';

const EMPTY_PREVIEW: AudiencePreviewState = {
  count: null,
  totalReach: null,
  isProspecting: false,
  modelStatus: null,
  fitRequestedAt: null,
  isPending: true,
  error: null,
  incomplete: null,
  rules: null,
  segments: [],
};

/** The audience the list's Duplicate handed over in sessionStorage, if it is one. */
function isStoredAudience(value: unknown): value is Audience {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.id === 'string'
    && typeof record.name === 'string'
    && typeof record.audience_type === 'string'
    && record.rules !== null && typeof record.rules === 'object';
}

type OpenSections = { name: boolean; audience: boolean; review: boolean };

export interface NewAudienceFlowProps {
  /** Called with the saved audience's id; the route takes the operator back to the list, which
   * names the new audience in its "created" banner. */
  onCreated: (audienceId: string) => void;
  /** An audience to start from, as `/audiences/new?duplicate=<id>` names it. */
  duplicateId?: string;
}

function NewAudienceFlow({ onCreated, duplicateId }: NewAudienceFlowProps) {
  const audienceQuery = useAudiences();
  const productionQuery = useProductions();
  const createAudience = useCreateAudience();
  const audiences = audienceQuery.data ?? [];
  const productions = productionQuery.data ?? [];

  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [type, setType] = useState<VisualAudienceType>('Direct');
  const [groups, setGroups] = useState<VisualAudienceGroup[]>(() => defaultGroupsForType('Direct'));
  const [duplicatedFrom, setDuplicatedFrom] = useState<{ id: string; name: string } | null>(null);
  const [preview, setPreview] = useState<AudiencePreviewState>(EMPTY_PREVIEW);
  const appliedDuplicate = useRef<string | null>(null);

  const [open, setOpen] = useState<OpenSections>({ name: true, audience: false, review: false });
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [audConfirmed, setAudConfirmed] = useState(false);
  const required = useRequiredFeedback();

  const duplicate = useCallback((audience: Audience | null | undefined) => {
    required.clearAll();
    if (!audience) {
      setDuplicatedFrom(null);
      setName('');
      setDesc('');
      setType('Direct');
      setGroups(defaultGroupsForType('Direct', { productions, audiences }));
      setAudConfirmed(false);
      return;
    }
    const nextType = visualTypeFromAudienceType(audience.audience_type);
    setDuplicatedFrom({ id: audience.id, name: audience.name });
    setName(`${audience.name} (copy)`);
    setDesc(audience.description ?? '');
    setType(nextType);
    setGroups(visualGroupsFromCanonical(audience.rules));
    setAudConfirmed(false);
  }, [audiences, productions, required.clearAll]);

  useEffect(() => {
    if (!duplicateId || appliedDuplicate.current === duplicateId) return;
    let source = audiences.find((audience) => audience.id === duplicateId);
    if (!source) {
      try {
        const stored: unknown = JSON.parse(
          sessionStorage.getItem('sam:audience-duplicate') || 'null',
        );
        if (isStoredAudience(stored) && stored.id === duplicateId) source = stored;
      } catch {
        // Ignore corrupt or storage-blocked handoff data.
      }
    }
    if (!source) return;
    appliedDuplicate.current = duplicateId;
    duplicate(source);
    try {
      sessionStorage.removeItem('sam:audience-duplicate');
    } catch {
      // Storage can be disabled without affecting the active-list path.
    }
  }, [audiences, duplicate, duplicateId]);

  const onPreviewChange = useCallback((snapshot: AudiencePreviewState) => {
    setPreview((current) => {
      if (
        current.count === snapshot.count
        && current.totalReach === snapshot.totalReach
        && current.isProspecting === snapshot.isProspecting
        && current.modelStatus === snapshot.modelStatus
        && current.fitRequestedAt === snapshot.fitRequestedAt
        && current.isPending === snapshot.isPending
        && current.error === snapshot.error
        && current.rules === snapshot.rules
        && current.segments === snapshot.segments
      ) return current;
      return snapshot;
    });
    if (
      snapshot.rules !== null
      && snapshot.count !== null
      && !snapshot.isPending
      && !snapshot.error
    ) {
      required.clear('audience-definition');
    }
  }, [required.clear]);

  const toggle = (key: keyof OpenSections) => setOpen((current) => ({ ...current, [key]: !current[key] }));
  const totalRules = useMemo(
    () => groups.reduce((count, group) => count + group.rules.length, 0),
    [groups],
  );
  // A lookalike whose fit is still being computed is saveable: saving is what
  // used to be the only fit trigger, and blocking on the fit here was a
  // deadlock. The count stays null until the fit publishes; the review gauge
  // shows the fitting-in-progress treatment instead of a number.
  const lookalikeFitPendingNow = preview.isProspecting
    && lookalikeFitPending(preview.modelStatus);
  const hasValidPreview = preview.rules !== null
    && !preview.isPending
    && !preview.error
    && (preview.count !== null || lookalikeFitPendingNow);
  const canSave = name.trim().length > 0
    && totalRules > 0
    && hasValidPreview
    && !createAudience.isPending;

  const audienceIssue = () => {
    if (preview.error) {
      return preview.error.message || 'Fix the highlighted audience rule before continuing.';
    }
    if (totalRules === 0 || preview.rules === null) {
      return 'Add at least one complete audience rule before continuing.';
    }
    if (preview.isPending || (preview.count === null && !lookalikeFitPendingNow)) {
      return 'Wait for the live audience estimate to finish before continuing.';
    }
    return null;
  };

  const validateForSave = () => {
    const definitionMessage = audienceIssue();
    const issues: (RequiredIssue | null)[] = [
      name.trim() ? null : {
        key: 'audience-name',
        message: 'Enter an audience name before saving.',
      },
      definitionMessage ? {
        key: 'audience-definition',
        message: definitionMessage,
      } : null,
    ];
    const first = issues.find((issue) => issue !== null);
    if (first?.key === 'audience-name') {
      setOpen((current) => ({ ...current, name: true }));
    } else if (first?.key === 'audience-definition') {
      setOpen((current) => ({ ...current, audience: true }));
    }
    return required.report(issues);
  };

  const save = async () => {
    if (createAudience.isPending || !validateForSave() || !preview.rules) return;
    try {
      const created = await createAudience.mutateAsync({
        id: audienceIdForCreate(name),
        name: name.trim(),
        description: desc.trim() || null,
        audience_type: audienceTypeFromVisualType(type),
        rules: preview.rules,
      });
      onCreated(created.id);
    } catch {
      // MutationErrorLine renders the typed API failure in both save locations.
    }
  };

  const previewPercent = preview.count !== null
    && preview.totalReach !== null
    && preview.totalReach > 0
    ? (preview.count / preview.totalReach) * 100
    : null;

  return (
    <div>
      <div className="page-hd">
        <div>
          <h1 className="page-title">New Audience</h1>
          <div className="page-sub" style={{marginTop:6}}>Build a reusable segment from CRM, behavior, or lookalike rules</div>
        </div>
        <PageActions>
          <Link className="btn btn-ghost btn-icon-phone" data-phone="inline" aria-label="Cancel" to="/audiences" preload="intent">{Icons.x} <span className="phone-long">Cancel</span></Link>
          <button
            className="btn btn-primary"
            data-phone="inline"
            onClick={() => void save()}
            disabled={createAudience.isPending}
            title={!canSave ? 'Name and a successful live audience estimate are required' : 'Save audience'}
          >
            {Icons.check} {createAudience.isPending ? 'Saving…' : <><span className="phone-long">Save audience</span><span className="phone-short">Save</span></>}
          </button>
        </PageActions>
      </div>

      <div className="create-wrap" style={{gridTemplateColumns:'1fr'}}>
        <div className="create-main" style={{maxWidth:900,margin:'0 auto',width:'100%'}}>
          <div className="facet" style={{margin:'0 0 14px',padding:'10px 12px',background:'var(--bg-sunk)'}}>
            <div className="facet-hd" style={{marginBottom:6}}>
              <h5 style={{fontSize:10}}>Quick start · duplicate from existing</h5>
              <span className="muted" style={{fontSize:10}}>Optional · clones type, canonical rules, description, and name</span>
            </div>
            {audienceQuery.error ? (
              <QueryErrorNote
                error={audienceQuery.error}
                what="saved audiences"
                onRetry={() => void audienceQuery.refetch()}
              />
            ) : (
              <div className="flex gap-2 items-center" style={{flexWrap:'wrap'}}>
                <div style={{flex:'1 1 320px',minWidth:260}}>
                  <SearchableDropdown
                    value={duplicatedFrom?.id || ''}
                    onChange={(id) => duplicate(audiences.find((audience) => audience.id === id))}
                    options={audiences.map((audience) => ({
                      id: audience.id,
                      label: audience.name,
                      sublabel: `${visualTypeFromAudienceType(audience.audience_type)} · ${(audience.estimated_size ?? 0).toLocaleString()} people`,
                    }))}
                    placeholder={audienceQuery.isLoading ? 'Loading saved audiences…' : '— Start blank —'}
                    emptyOptionLabel="— Start blank —"
                    emptyOptionValue=""
                    searchPlaceholder={`Search ${audiences.length} saved audiences…`}
                  />
                </div>
                {duplicatedFrom && (
                  <span className="tag" style={{background:'color-mix(in oklch, var(--accent) 14%, transparent)',borderColor:'transparent',color:'var(--ink)'}}>
                    Duplicated from <b style={{marginLeft:4}}>{duplicatedFrom.name}</b>
                    <button type="button" className="x" style={{marginLeft:6,cursor:'pointer',background:'none',border:0,padding:0,color:'inherit'}} aria-label="Start blank instead" onClick={() => duplicate(null)}>{Icons.x}</button>
                  </span>
                )}
              </div>
            )}
          </div>

          <Section
            n="1"
            title="Name & Description"
            summary={nameConfirmed && !open.name ? (name || 'Untitled audience') + (desc ? ` · ${desc.slice(0, 80)}` : '') : 'Give this audience a recognizable name'}
            open={open.name}
            done={nameConfirmed && !!name.trim()}
            onToggle={() => toggle('name')}
          >
            <RequiredField
              fieldKey="audience-name"
              message={required.messages['audience-name']}
              attempt={required.attempt}
              className="field"
            >
              <label className="field-lbl">Audience name <span style={{color:'var(--live)'}}>*</span></label>
              <input
                className="input"
                placeholder="e.g. Premium Buyers · NYC ZIPs · 2yr window"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (event.target.value.trim()) required.clear('audience-name');
                }}
              />
            </RequiredField>
            <div className="field">
              <label className="field-lbl">Description <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:8,fontSize:10.5}}>Optional · helps teammates understand intent</span></label>
              <textarea className="input" rows={2} placeholder="What's this audience for? Who's in it? Why these rules?" value={desc} onChange={(event) => setDesc(event.target.value)}/>
            </div>
            <div className="flex between items-center mt-2" style={{gap:10}}>
              <div className="muted" style={{fontSize:11.5}}>
                {!name.trim() ? 'Enter a name to continue.' : <><span style={{color:'var(--good)'}}>{Icons.check}</span> Ready.</>}
              </div>
              <button className="btn btn-primary" onClick={() => {
                if (!name.trim()) {
                  required.report([{
                    key: 'audience-name',
                    message: 'Enter an audience name to continue.',
                  }]);
                  return;
                }
                required.clear('audience-name');
                setNameConfirmed(true);
                setOpen({...open,name:false,audience:true});
              }}>
                {Icons.check} Confirm name
              </button>
            </div>
          </Section>

          <Section
            n="2"
            title="Audience"
            summary={audConfirmed && !open.audience
              ? `${type} · ${groups.length} group${groups.length === 1 ? '' : 's'} · ${totalRules} rule${totalRules === 1 ? '' : 's'} · ${preview.count?.toLocaleString() ?? '—'} matched`
              : 'Pick a type and build the rule logic'}
            open={open.audience}
            done={audConfirmed && hasValidPreview}
            onToggle={() => toggle('audience')}
          >
            {productionQuery.error && (
              <QueryErrorNote
                error={productionQuery.error}
                what="productions"
                onRetry={() => void productionQuery.refetch()}
              />
            )}
            <RequiredField
              fieldKey="audience-definition"
              message={required.messages['audience-definition']}
              attempt={required.attempt}
              surface
            >
              <AudienceBuilder
                production={null}
                productions={productions}
                confirmed={audConfirmed}
                audiences={audiences}
                type={type}
                setType={(next) => {
                  required.clear('audience-definition');
                  setType(next);
                }}
                groups={groups}
                setGroups={(next) => {
                  required.clear('audience-definition');
                  setGroups(next);
                }}
                hideSavedPicker
                onPreviewChange={onPreviewChange}
                onConfirm={() => {
                  required.clear('audience-definition');
                  setAudConfirmed(true);
                  setOpen({...open,audience:false,review:true});
                }}
                onEdit={() => setAudConfirmed(false)}
              />
            </RequiredField>
          </Section>

          <Section n="3" title="Review & Save" summary="Final check before saving" open={open.review} done={false} onToggle={() => toggle('review')}>
            <div className="grid-2 mb-3" style={{gap:14}}>
              <div className="facet" style={{margin:0}}>
                <div className="facet-hd"><h5>Name</h5></div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:15,lineHeight:1.25}}>{name || <span className="muted">(missing)</span>}</div>
                {desc && <div className="muted mt-1" style={{fontSize:11.5}}>{desc}</div>}
              </div>
              <div className="facet" style={{margin:0}}>
                <div className="facet-hd"><h5>Type</h5></div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:15}}>{type}</div>
                <div className="muted mt-1" style={{fontSize:11.5}}>{TYPE_BLURB[type]}</div>
              </div>
              <div className="facet" style={{margin:0}}>
                <div className="facet-hd"><h5>Rules</h5></div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:15}}>{groups.length} group{groups.length === 1 ? '' : 's'} · {totalRules} rule{totalRules === 1 ? '' : 's'}</div>
                <div className="muted mt-1" style={{fontSize:11.5}}>Groups OR together · rules AND within group</div>
              </div>
              <AudienceSizeGauge
                compact
                size={preview.count}
                totalReach={preview.totalReach}
                loading={preview.isPending}
                error={preview.error}
                incomplete={preview.incomplete}
                modelStatus={preview.isProspecting ? preview.modelStatus : null}
                fitRequestedAt={preview.isProspecting ? preview.fitRequestedAt : null}
              />
            </div>

            <div className="flex between items-center mt-2" style={{gap:10}}>
              <div className="muted" style={{fontSize:11.5}}>
                {canSave
                  ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> Ready to save.</>
                  : preview.isPending
                    ? 'Waiting for the live estimate…'
                    : preview.error
                      ? 'Resolve the preview error before saving.'
                      : 'Add a name and at least one valid rule before saving.'}
              </div>
              <div className="flex gap-2">
                <Link className="btn" to="/audiences" preload="intent">Cancel</Link>
                <button className="btn btn-primary" disabled={createAudience.isPending} onClick={() => void save()}>
                  {Icons.check} {createAudience.isPending ? 'Saving…' : 'Save audience'}
                </button>
              </div>
            </div>
            {createAudience.error && <div style={{marginTop:10}}><MutationErrorLine error={createAudience.error}/></div>}
            {previewPercent !== null && (
              <div className="muted mono" style={{marginTop:8,fontSize:10.5}}>
                Live estimate covers {previewPercent.toFixed(1)}% of the current reachable universe.
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

export { NewAudienceFlow };
